import { Component, HostListener, OnInit } from '@angular/core';
import { FormBuilder, FormGroup, UntypedFormGroup } from '@angular/forms';
import { MatSnackBar } from '@angular/material/snack-bar';

import { forkJoin, map, Observable } from 'rxjs';

import { ITreatmentPreferenceDatum, TreatmentPreferencesService } from '@sondermind/data-access/provider-facing/provider-profile';
import { ReferenceDataHttpService, ReferenceDataTypes, ReferenceDatum } from '@sondermind/data-access/reference-data';
import { SondermindApplicationService } from '@sondermind/data-access/sondermind-app-service';
import { IComponentCanDeactivate } from '@sondermind/features/provider-facing/provider-profile';

import { IrisButtonTypes } from '@sondermind/iris/button';

@Component({
  selector: 'member-profile-approaches',
  templateUrl: './profile-approaches.component.html',
  styleUrls: ['./profile-approaches.component.scss', '../../modules/profile/profile-app-shared-styles.scss'],
  standalone: false
})
export class ProfileApproachesPage implements OnInit, IComponentCanDeactivate {
  approaches: ReferenceDatum[] = [];
  userTreatmentPrefs: ITreatmentPreferenceDatum[] = [];

  approachesForm: UntypedFormGroup;
  approachesFormReset: any = null;

  saving: boolean = false;
  loaded: boolean = false;

  irisButtonTypes = IrisButtonTypes;

  constructor(
    private formBuilder: FormBuilder,
    private appService: SondermindApplicationService,
    private treatmentPrefsService: TreatmentPreferencesService,
    private snackBar: MatSnackBar,
    private refDataService: ReferenceDataHttpService
  ) {}

  ngOnInit(): void {
    this.loadData();
  }

  // @HostListener allows us to also guard against browser refresh, close, etc.
  @HostListener('window:beforeunload')
  canDeactivate(): boolean {
    return this.approachesForm?.pristine;
  }

  getApproachesObservable(): Observable<ReferenceDatum[]> {
    return this.refDataService.list(
      ReferenceDataTypes.treatment_preferences,
      {
        filter: [{ key: 'hidden', value: 'false' }],
        sort: [{ key: 'title', asc: true }],
        page: 'disabled'
      }
    ).pipe(
      map((response) => response.data),
    );
  }

  getUserTreatmentPrefsObservable(): Observable<ITreatmentPreferenceDatum[]> {
    return this.treatmentPrefsService.index({ page: 'disabled' }).pipe(
      map((response) => response.data)
    );
  }

  loadData(): void {
    forkJoin({
      approaches: this.getApproachesObservable(),
      userTreatmentPrefs: this.getUserTreatmentPrefsObservable()
    }).subscribe({
      next: (results) => {
        this.approaches = results.approaches;
        this.userTreatmentPrefs = results.userTreatmentPrefs;
        this.processInitialResponse();
        this.loaded = true;
      },
      error: (err) => {
        // eslint-disable-next-line no-console
        console.error(err);
      }
    });
  }

  processInitialResponse(): void {
    this.approachesForm = this.formBuilder.group({
      approaches: this.formBuilder.group({})
    });

    const approachesFormGroup = this.approachesForm.controls.approaches as FormGroup;
    this.approaches.forEach((approach) => {
      const isSelected = this.userTreatmentPrefs.some((u) => u.reference_data_id === approach.id);
      const ctrl = this.formBuilder.control(isSelected);
      approachesFormGroup.addControl(approach.id.toString(), ctrl);
    });

    this.approachesFormReset = this.approachesForm.value;
  }

  onCancel(): void {
    this.approachesForm.patchValue(this.approachesFormReset);
    this.approachesForm.markAsPristine();
  }

  onSave(): void {
    if (this.saving || this.approachesForm.invalid) {
      this.snackBar.open(
        'Error saving. Please check required fields on Approaches.',
        'Dismiss',
        { duration: 30000, panelClass: 'toast-color' }
      );
      return;
    }

    this.saving = true;

    const formValues = this.approachesForm?.controls?.approaches?.value as Record<string, boolean>;
    const savedPrefs: Array<{ id: number; }> = Object.keys(formValues)
      .filter((x) => formValues[x])
      .map((x) => ({
        id: +x
      }));

    this.treatmentPrefsService.update(savedPrefs, this.appService.currentUser.personaProviderId).subscribe({
      next: () => {
        this.approachesForm.markAsPristine();
        this.approachesFormReset = this.approachesForm.value;

        this.saving = false;

        // kick off reload so we get new profile submission errors
        this.appService.reloadCurrentUser();
        this.snackBar.open('Your treatment approaches have been saved.', 'Dismiss');
      },
      error: () => {
        this.snackBar.open('Treatment approaches save failed, please try again', 'Dismiss');
        this.saving = false;
      }
    });
  }
}
